import Table from "@/components/Table/table";
import {
  useListCalculationStepsQuery,
  useReadDistributionProcessingQuery,
} from "@/services/distribution/distributionReducers";
import { useLazyCheckAndReorderStepQuery } from "./calculationStepReducer";
import {
  MotifButton,
  MotifProgressLoader,
  MotifToast,
} from "@ey-xd/motif-react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useColumnDistributionSteps } from "./constants/columnDistributionSteps";
import { useNavigate } from "react-router-dom";
import { useCase } from "@/features/case/use-case";
import { useDispatch, useSelector } from "react-redux";
import GenerateDraftCalculation from "../summery/GenerateDraftCalculation";
import {
  customPaginationPageSize,
  customPaginationPageSizeSelector,
  customTableLoadingData,
} from "@/components/Table/CustomPaginationConstants";
import { useContext, useEffect, useState, useRef, useMemo } from "react";
import CustomPagination from "@/components/Pagination/CustomPagination";
import { useTranslation } from "react-i18next";
import { havingAddPermission, havingEditPermission } from "@/utility/common";
import DistributionContext from "../summery/DistributionContext";
import {
  CREATE_BUTTON_VARIANT,
  GetCommonDownArrowImgButton,
  GetCommonUpArrowImgButton,
} from "@/components/Images/CommonImages";
import NotificationGrid from "@/components/Notification/NotificationGrid";
import { ActiveTabsOrder } from "@/menuData";
import { DistributionsTab } from "@/pages/CaseDetail/Distributions/DistributionsType";
import { setCurrentTab } from "@/pages/CaseDetail/Distributions/DistributionsSlice";
import { setComponentState1 } from "@/features/retain_state/retainStateSlice";

const allStepsPageSize = 2147483647;
const TitleComplement: React.FC<{
  CalculationStep: string[];
  processingStatus: any;
}> = ({ CalculationStep, processingStatus }) => {
  const distributionContextData: any = useContext<any>(DistributionContext);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { distributionId, caseId } = useParams<{
    caseId: string;
    distributionId: string;
  }>();
  return havingAddPermission(CalculationStep) ? (
    <MotifButton
      onClick={() => {
        navigate(
          `/case/${caseId}/distribution/${distributionId}/calculation-step/create`,
          {
            state: { distributionId },
          }
        );
      }}
      size="small"
      type="submit"
      variant={CREATE_BUTTON_VARIANT}
      disabled={
        distributionContextData?.distributionStatus === 3 ||
        [1, 2]?.includes(processingStatus)
      }
    >
      {t("pages.distributions.distributionStepsTab.addNewStep")}{" "}
    </MotifButton>
  ) : (
    <></>
  );
};

const DistributionSteps = () => {
  const caseData = useCase();
  const permissions = useSelector((state: any) => state.permissions);
  const { CalculationStep } = permissions;
  const dispatch = useDispatch();
  const { distributionId } = useParams();
  const { data: processingData } = useReadDistributionProcessingQuery(
    distributionId,
    {
      skip: !distributionId,
    }
  );
  const retainState = useSelector(
    (e: any) => e.retainState.stepDistributionTabState
  );
  const [searchParams] = useSearchParams();
  const navigatedFrom = searchParams.get("navigatedFrom");
  const [orderErrorMessage, setOrderErrorMessage] = useState("");
  const paginationPageSize: number = customPaginationPageSize;
  const paginationPageSizeSelector: number[] = customPaginationPageSizeSelector;
  const { t } = useTranslation();
  const distributionContextData: any = useContext<any>(DistributionContext);
  const [checkAndReorderStep] = useLazyCheckAndReorderStepQuery();
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    supportingMessage: "",
    type: "success",
  });
  const latestDataRef = useRef<any>(null);

  useEffect(() => {
    // this method will make edit distribution to false when we navigate to other tab
    distributionContextData.setEditDistribution(false);
  }, [distributionContextData]);
  let tableLoadingData = customTableLoadingData;
  const getPaginationObj: any = useMemo(() => {
    let value: any = {
      distributionId: distributionId,
      PageNumber: retainState?.PageNumber || 1,
      PageSize: retainState?.PageSize || paginationPageSize,
      SearchText: retainState?.SearchText || "",
      SortOrder: retainState?.SortOrder || "ASC",
      SortColumn: retainState?.SortColumn || "SortOrder",
    };
    return value;
  }, [distributionId, paginationPageSize, retainState]);
  const paginationObj = getPaginationObj;

  function handlePaginationData(data: any, columnTypesData: any) {
    dispatch(
      setComponentState1({
        entityName: "stepDistributionTabState",
        entityState: {
          SortOrder: data.SortOrder,
          SortColumn: data.SortColumn,
          sortedColumnTypes: columnTypesData,
        },
      })
    );
  }
  const columnDistributionSteps = useColumnDistributionSteps(
    processingData?.processingStatus
  );

  const { data, isLoading, isFetching, error, refetch, status } =
    useListCalculationStepsQuery(paginationObj);

  useEffect(() => {
    latestDataRef.current = data;
  }, [data]);

  const reorderSteps = async (
    precedingStepId: string,
    succeedingStepId: string
  ) => {
    setOrderErrorMessage("");
    await checkAndReorderStep({ precedingStepId, succeedingStepId })
      .then((response: any) => {
        if (response.data) {
          refetch();
        } else if (response?.data === false) {
          setOrderErrorMessage(
            t("pages.distributions.distributionStepsTab.stepOrderValidation")
          );
        } else if (response?.error?.data?.message) {
          setOrderErrorMessage(response?.error?.data?.message);
        } else {
          setOrderErrorMessage(t("global.globalNotifications.wentWrong"));
        }
      })
      .catch((error: any) => {
        setOrderErrorMessage(t("global.globalNotifications.wentWrong"));
      });
  };

  const handleShiftRow = (
    direction: "up" | "down",
    params: any
  ) => {
    const latestData = latestDataRef.current;
    if (isFetching || !latestData?.results?.length) return;
    const rowIndex = latestData.results.findIndex(
      (step: any) => step.id === params.data.id
    );
    if (rowIndex < 0) return;
    const updatedData = [...latestData?.results];
    const isSortOrderAscending =
      paginationObj.SortColumn?.toLowerCase() === "sortorder" &&
      paginationObj.SortOrder?.toLowerCase() === "asc";
    if (!isSortOrderAscending) return;

    if (direction === "up" && rowIndex > 0) {
      const precedingStepId = params.data.id;
      const succeedingStepId = updatedData[rowIndex - 1].id;
      reorderSteps(precedingStepId, succeedingStepId);
    } else if (direction === "up" && latestData.previousBoundaryStepId) {
      reorderSteps(params.data.id, latestData.previousBoundaryStepId);
    } else if (
      direction === "down" &&
      rowIndex < updatedData.length - 1
    ) {
      const succeedingStepId = params.data.id;
      const precedingStepId = updatedData[rowIndex + 1].id;
      reorderSteps(precedingStepId, succeedingStepId);
    } else if (direction === "down" && latestData.nextBoundaryStepId) {
      reorderSteps(latestData.nextBoundaryStepId, params.data.id);
    }
  };

  const getCaseRenderers = (processingStatus: any) => ({
    orderCellRenderer: function (params: any) {
      return (
        <>
          <Link
            to={`/case/${
              caseData.caseId
            }/distribution/${distributionId}/calculation-step/${
              params.data.id
            }/step-summary${
              navigatedFrom === "paymentGrid"
                ? "?navigatedFrom=paymentGrid"
                : ""
            }`}
          >
            <span className="text-bold-underline">{params.data.sortOrder}</span>
          </Link>
        </>
      );
    },
    stepNameRenderer: function (params: any) {
      return (
        <>
          <Link
            to={`/case/${
              caseData.caseId
            }/distribution/${distributionId}/calculation-step/${
              params.data.id
            }/step-summary${
              navigatedFrom === "paymentGrid"
                ? "?navigatedFrom=paymentGrid"
                : ""
            }`}
          >
            <span className="text-bold-underline">{params.data.name}</span>
          </Link>
        </>
      );
    },
    payeeTypeRenderer: function (params: any) {
      return !(
        (params?.data?.calculationGroupBy === 0 ||
          params?.data?.calculationGroupBy === 2) &&
        params?.data?.payeeTypeId === null
      ) ? (
        <Link
          to={`/case/${caseData.caseId}/configuration/payee/edit/${params.data.payeeTypeId}?navigatedFrom=distributionSteps&distributionId=${distributionId}&distributionName=${distributionContextData.distributionName}`}
        >
          <span className="text-bold-underline">
            {params.data.payeeTypeName}
          </span>
        </Link>
      ) : (
        "Creditor"
      );
    },
    subAccountRenderer: function (params: any) {
      return (
        <Link
          to={`/case/${caseData.caseId}/?activeTab=${ActiveTabsOrder.DISTRIBUTION}`}
          onClick={() => {
            dispatch(setCurrentTab(DistributionsTab.AccountSetUp));
          }}
        >
          <span className="text-bold-underline">
            {params.data.subAccountName}
          </span>
        </Link>
      );
    },
    sortingCellRenderers: function (params: any) {
      const rowIndex =
        data?.results?.findIndex((step: any) => step.id === params.data.id) ??
        -1;
      const sortOrderIsAscending =
        paginationObj.SortColumn?.toLowerCase() === "sortorder" &&
        paginationObj.SortOrder?.toLowerCase() === "asc";
      return (
        <>
          <GetCommonUpArrowImgButton
            onClick={() => {
              handleShiftRow("up", params);
            }}
            disabled={
              isFetching ||
              !sortOrderIsAscending ||
              rowIndex < 0 ||
              (rowIndex === 0 &&
                (paginationObj.PageNumber === 1 ||
                  !data?.previousBoundaryStepId)) ||
              distributionContextData?.distributionStatus === 3 ||
              !havingEditPermission(CalculationStep) ||
              [1, 2].includes(params.processingStatus)
            }
          ></GetCommonUpArrowImgButton>
          <GetCommonDownArrowImgButton
            onClick={() => {
              handleShiftRow("down", params);
            }}
            disabled={
              isFetching ||
              !sortOrderIsAscending ||
              rowIndex < 0 ||
              (rowIndex === (data?.results?.length ?? 0) - 1 &&
                !data?.nextBoundaryStepId) ||
              distributionContextData?.distributionStatus === 3 ||
              !havingEditPermission(CalculationStep) ||
              [1, 2].includes(params.processingStatus) 
            }
          ></GetCommonDownArrowImgButton>
        </>
        // <div className="motif-row">
        //   <div className="motif-col-lg-6">
        //     <CommonTooltipWrapper
        //       tooltipTitle={t("global.globalActions.moveUp")}
        //     >
        //       <MotifButton
        //         size="small"
        //         variant="secondary"
        //         type="button"
        //         onClick={() => {
        //           handleShiftRow(params.rowIndex, "up", params);
        //         }}
        //         disabled={
        //           !havingEditPermission(CalculationStep) ||
        //           distributionStatus === 3
        //         }
        //       >
        //         <MotifIcon iconFunction={IconoirArrowUp} size="24" />
        //       </MotifButton>
        //     </CommonTooltipWrapper>
        //   </div>
        //   <div className="motif-col-lg-6">
        //     <CommonTooltipWrapper
        //       tooltipTitle={t("global.globalActions.moveDown")}
        //     >
        //       <MotifButton
        //         onClick={() => {
        //           handleShiftRow(params.rowIndex, "down", params);
        //         }}
        //         size="small"
        //         type="button"
        //         variant="secondary"
        //         disabled={
        //           !havingEditPermission(CalculationStep) ||
        //           distributionStatus === 3
        //         }
        //       >
        //         <MotifIcon iconFunction={IconoirArrowDown} size="24" />
        //       </MotifButton>
        //     </CommonTooltipWrapper>
        //   </div>
        // </div>
      );
    },
  });

  const renderMessage = () => {
    if (notification.show) {
      return (
        <div className="mar-b-10">
          <MotifToast
            onClose={() => setNotification({ ...notification, show: false })}
            //@ts-ignore
            variant={notification.type}
            supportingText={
              [1, 2].includes(processingData?.processingStatus) &&
              notification.type === "info"
                ? t("global.globalNotifications.progressingMessage", {
                    status: processingData?.processingStatusMessage,
                  })
                : ""
            }
          >
            {notification.type === "error" ? (
              (
                <>
                  <div>{t(notification.message)}</div>
                </>
              ) || <div>{t("global.globalNotifications.unExpectedError")}</div>
            ) : notification.type === "success" ? (
              (
                <>
                  <div>{t(notification.message)}</div>
                </>
              ) || (
                <div>{t("global.globalNotifications.updatedSuccessfully")}</div>
              )
            ) : (
              <>
                <div>{t(notification.message)}</div>
              </>
            )}

            {/* {[1, 2].includes(processingData?.processingStatus) &&
              notification.type === "info" && (
                <div>Status: {processingData?.processingStatusMessage} </div>
              )} */}
          </MotifToast>
        </div>
      );
    }
  };

  function handleSummaryApiAfterSettlementDone() {
    refetch();
  }

  return (
    <>
      {isLoading ||
      distributionContextData?.distributionStatus === undefined ? (
        <MotifProgressLoader show variant="default" className="mar-b-20" />
      ) : (
        <>
          {renderMessage()}
          {orderErrorMessage && (
            <NotificationGrid message={orderErrorMessage} type="error" />
          )}
          <div className="mar-b-10">
            <Table
              columnDefs={columnDistributionSteps}
              columnTypes={{ ...retainState.sortedColumnTypes }}
              handlePaginationData={handlePaginationData}
              paginationObj={paginationObj}
              cellRenderers={data?.results ?? []}
              TitleComplement={() => (
                <TitleComplement
                  CalculationStep={CalculationStep}
                  processingStatus={processingData?.processingStatus}
                />
              )}
              caseRenderers={getCaseRenderers(processingData?.processingStatus)}
              title={"pages.distributions.distributionStepsTab.tableTitle"}
              description={
                "pages.distributions.distributionStepsTab.tableDescription"
              }
              pagination={false}
              // onRowClicked={handleRowClick}
              overlayNoRowsTemplate={
                isFetching
                  ? t(tableLoadingData)
                  : error
                  ? t("global.globalNotifications.wentWrong")
                  : t("global.globalNotifications.noRecords")
              }
            />

            <CustomPagination
              apiData={data}
              apiStatus={status}
              currentpage={paginationObj.PageNumber}
              pageSize={paginationObj.PageSize}
              allPageSize={allStepsPageSize}
              pageSizeSelector={[
                10,
                ...paginationPageSizeSelector,
                allStepsPageSize,
              ]}
              disableNext={!data?.hasMoreRecords}
              onChangePageSize={(page: number) => {
                dispatch(
                  setComponentState1({
                    entityName: "stepDistributionTabState",
                    entityState: {
                      PageSize: Number(page),
                      PageNumber: 1,
                    },
                  })
                );
              }}
              onChangeNextPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "stepDistributionTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber + 1,
                    },
                  })
                );
              }}
              onChangePreviousPage={() => {
                dispatch(
                  setComponentState1({
                    entityName: "stepDistributionTabState",
                    entityState: {
                      PageNumber: paginationObj.PageNumber - 1,
                    },
                  })
                );
              }}
            />
            <br></br>
            <br></br>
            <br></br>

          </div>
          <GenerateDraftCalculation
            handleSummaryApiAfterSettlementDone={
              handleSummaryApiAfterSettlementDone
            }
            distributionName={distributionContextData.distributionName}
            isPending={isLoading}
            setpsData={data?.results}
            processingData={processingData}
            setNotification={setNotification}
          />
        </>
      )}
    </>
  );
};

export default DistributionSteps;
